import React, { useState, useEffect, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Save, Loader2, FileText, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import TemplateFields from '@/components/TemplateFields';
import EventEmailEditor from '@/components/EventEmailEditor';
import PageContainer from '@/components/PageContainer';

/**
 * The four emails every show has, task 3.6 follow up. `tab` is the string
 * Radix Tabs wants; `kind` is what the API wants back ('initial' or a
 * reminder stage number). `subjectField`/`bodyField` are the columns on the
 * show itself, the same shape /api/shows already returns and accepts.
 */
const EMAIL_KINDS = [
  { tab: 'initial', kind: 'initial', label: 'Initial Email', subjectField: 'email_subject', bodyField: 'email_body' },
  { tab: '1', kind: 1, label: 'First Follow Up', subjectField: 'reminder_1_subject', bodyField: 'reminder_1_body' },
  { tab: '2', kind: 2, label: 'Second Follow Up', subjectField: 'reminder_2_subject', bodyField: 'reminder_2_body' },
  { tab: '3', kind: 3, label: 'Last Email', subjectField: 'reminder_3_subject', bodyField: 'reminder_3_body' },
];

const EMPTY_EVENT_TPL = {
  sms_template: '',
  email_subject: '', email_body: '',
  reminder_1_subject: '', reminder_1_body: '',
  reminder_2_subject: '', reminder_2_body: '',
  reminder_3_subject: '', reminder_3_body: '',
};

/** The two defaults keys the API returns ('initial', 'reminder_1', ...), matched to a tab. */
const defaultsKeyFor = (tab) => (tab === 'initial' ? 'initial' : `reminder_${tab}`);

/** The rep_config.TemplateSet keys for a tab ('emailSubject'/'emailBody' for Initial, 'reminderNSubject'/'reminderNBody' for a stage), matching worker/lib/templates.ts exactly. */
const repConfigKeysFor = (tab) => (tab === 'initial'
  ? { subjectKey: 'emailSubject', bodyKey: 'emailBody' }
  : { subjectKey: `reminder${tab}Subject`, bodyKey: `reminder${tab}Body` });

/**
 * What a blank subject/body box actually falls through to: show, then rep,
 * then the global default, then the built in wording, checked one level at a
 * time, the same order worker/lib/templates.ts's resolveTemplates uses. Null
 * means the box itself already has a value, there is nothing to preview.
 */
function resolveEmailField(showVal, repVal, defaultVal, builtInVal, repLabel) {
  if ((showVal || '').trim()) return null;
  if ((repVal || '').trim()) return { source: 'rep', value: repVal, label: `${repLabel}'s own template` };
  if ((defaultVal || '').trim()) return { source: 'default', value: defaultVal, label: 'the global default' };
  if (builtInVal != null) return { source: 'builtin', value: builtInVal, label: 'the built-in wording' };
  return { source: 'none', value: '', label: 'Nothing set at any level' };
}

/**
 * The text message has no literal built-in template to preview: the last
 * resort wording (buildSms in worker/lib/messages.ts) is assembled line by
 * line from the rep's real phone number, email and vCard, not rendered from
 * a fixed string, so there is nothing fixed to show once show, rep and
 * default are all exhausted.
 */
function resolveSmsField(showVal, repVal, defaultVal, repLabel) {
  if ((showVal || '').trim()) return null;
  if ((repVal || '').trim()) return { source: 'rep', value: repVal, label: `${repLabel}'s own template` };
  if ((defaultVal || '').trim()) return { source: 'default', value: defaultVal, label: 'the global default' };
  return { source: 'none', value: '', label: "Falls back to the built-in wording, assembled from the rep's own real info. Nothing fixed to preview." };
}

export default function Templates() {
  const { isAdmin, user } = useAuth();
  const [searchParams] = useSearchParams();
  const eventPanelRef = useRef(null);
  const [config, setConfig] = useState({ default: {}, reps: {} });
  const [selectedRep, setSelectedRep] = useState('');
  // Arriving from a show's own edit form (Shows.jsx: "Edit message wording
  // for this show") preselects that show here, rather than landing on a
  // blank Per-Event panel the owner then has to find it in again.
  const [selectedShow, setSelectedShow] = useState(() => searchParams.get('show') || '');
  const [eventTpl, setEventTpl] = useState(EMPTY_EVENT_TPL);
  const [activeEmailTab, setActiveEmailTab] = useState('initial');
  const [loading, setLoading] = useState(true);
  const [savingDefault, setSavingDefault] = useState(false);
  const [savingRep, setSavingRep] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);

  const loadConfig = async () => {
    try {
      const recs = await base44.entities.AppSettings.filter({ key: 'rep_config' });
      if (recs && recs.length > 0) setConfig(JSON.parse(recs[0].value || '{}'));
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { loadConfig(); }, []);

  // Only on the way in, once, so scrolling this panel manually later never
  // gets yanked back down by a re-render. Gated on `loading` rather than an
  // empty dependency array: the Per-Event card (and this ref) do not exist
  // in the DOM until the loading spinner is replaced by the real content, so
  // firing on mount found eventPanelRef.current still null every time and
  // silently scrolled nowhere. Caught only by checking window.scrollY in a
  // live production check, not by confirming the card's content was right.
  const scrolledRef = useRef(false);
  useEffect(() => {
    if (loading || scrolledRef.current) return;
    scrolledRef.current = true;
    if (searchParams.get('show')) eventPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const { data: shows = [] } = useQuery({
    queryKey: ['shows'],
    queryFn: () => base44.entities.TradeShow.list('-start_date', 50),
  });

  const { data: reps = [] } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const all = await base44.entities.User.list();
      // Not a person: nothing to build a template panel for.
      return all.filter(u => !u.is_device_account);
    },
    enabled: isAdmin,
  });

  // The built in wording for all four emails, what "Reset to industry
  // default" restores. Fetched once; it never changes while this page is open.
  const { data: emailDefaults } = useQuery({
    queryKey: ['reminder-defaults'],
    queryFn: () => base44.reminders.defaults(),
  });

  // Pick the first rep once the list arrives, so the panel is never blank.
  useEffect(() => {
    if (!selectedRep && reps.length > 0) setSelectedRep(reps[0].id);
  }, [selectedRep, reps]);

  const repName = (id) => {
    const found = reps.find(r => r.id === id);
    return found ? (found.full_name || found.email) : 'this rep';
  };

  useEffect(() => {
    if (!selectedShow) { setEventTpl(EMPTY_EVENT_TPL); return; }
    const s = shows.find(x => x.id === selectedShow);
    if (s) {
      setEventTpl({
        sms_template: s.sms_template || '',
        email_subject: s.email_subject || '', email_body: s.email_body || '',
        reminder_1_subject: s.reminder_1_subject || '', reminder_1_body: s.reminder_1_body || '',
        reminder_2_subject: s.reminder_2_subject || '', reminder_2_body: s.reminder_2_body || '',
        reminder_3_subject: s.reminder_3_subject || '', reminder_3_body: s.reminder_3_body || '',
      });
    }
  }, [selectedShow, shows]);

  const upsertConfig = async (mutator) => {
    const recs = await base44.entities.AppSettings.filter({ key: 'rep_config' });
    const cfg = recs && recs[0] ? JSON.parse(recs[0].value || '{}') : { default: {}, reps: {} };
    mutator(cfg);
    const value = JSON.stringify(cfg);
    if (recs && recs[0]) await base44.entities.AppSettings.update(recs[0].id, { value });
    else await base44.entities.AppSettings.create({ key: 'rep_config', value });
    setConfig(cfg);
  };

  const saveDefault = async () => {
    setSavingDefault(true);
    try {
      await upsertConfig((cfg) => { cfg.default = config.default || {}; });
      toast.success('Default templates saved');
    } catch (e) { toast.error('Failed to save'); }
    setSavingDefault(false);
  };

  const saveRep = async () => {
    setSavingRep(true);
    try {
      await upsertConfig((cfg) => {
        cfg.reps = cfg.reps || {};
        cfg.reps[selectedRep] = (config.reps && config.reps[selectedRep]) || {};
      });
      toast.success(`${repName(selectedRep)} templates saved`);
    } catch (e) { toast.error('Failed to save'); }
    setSavingRep(false);
  };

  const saveEvent = async () => {
    if (!selectedShow) return;
    setSavingEvent(true);
    try {
      await base44.entities.TradeShow.update(selectedShow, { ...eventTpl });
      toast.success('Event templates saved');
    } catch (e) { toast.error('Failed to save'); }
    setSavingEvent(false);
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center">
          <ShieldCheck className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-foreground mb-1">Admins only</h1>
          <Link to="/"><Button variant="outline">Back to app</Button></Link>
        </div>
      </div>
    );
  }

  const repCfg = (config.reps && config.reps[selectedRep]) || {};
  const defaultCfg = config.default || {};
  const selectedRepUser = reps.find(r => r.id === selectedRep);
  const selectedShowObj = shows.find(s => s.id === selectedShow) || null;
  const selectedShowName = selectedShowObj?.name || 'this event';

  // Task: real rep data in the Templates preview (2026-09-24). Whichever
  // rep is selected in Per-Rep Override above, the same rep the Per-Event
  // panel's own inherited-value preview already names; the logged in admin
  // themselves only as a fallback, for the moment before the reps list has
  // loaded and selectedRep is still empty.
  const previewRepId = selectedRep || user?.id || null;

  // Task: real show data in the Per-Event Override preview (2026-09-24).
  // Only this panel has a real show in scope at all (Default and Per-Rep
  // apply globally, no one show to name), so only its own EventEmailEditor
  // instances below get this; every other preview keeps SAMPLE_SHOW's own
  // fake name and location exactly as before.
  const previewShow = selectedShowObj ? { name: selectedShowObj.name, location: selectedShowObj.location } : null;

  // Where "back" goes. Reads the URL's own ?show=, not the selectedShow
  // state below: that state can change while the admin browses the
  // per-event picker on this page, but back should still return to
  // whichever event this screen was actually opened from (Shows.jsx's
  // "Edit message wording for this show" link, `/templates?show=<id>`).
  // Shows.jsx reopens that event's own Edit Show form when it sees this
  // same ?show= param (see its own useEffect), so this is a round trip,
  // not just "back to the list". No ?show= means this was opened from
  // Admin's plain "Templates" button, so /admin is still correct there.
  const showIdFromUrl = searchParams.get('show');
  const backTo = showIdFromUrl ? `/shows?show=${showIdFromUrl}` : '/admin';

  return (
    <div className="min-h-screen bg-background safe-top pb-24">
      <PageContainer size="lg" className="px-4 py-6">
        <div className="flex items-center gap-3 mb-4">
          <Link to={backTo}><Button variant="outline" className="min-h-9 px-4 text-sm">Back</Button></Link>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-foreground">Message Templates</h1>
            <p className="text-sm text-muted-foreground">Default, per-rep & per-event overrides</p>
          </div>
        </div>

        <div className="mb-4 p-3 rounded-xl bg-accent/10 border border-accent/20">
          <p className="text-xs font-medium text-foreground">Which wording wins</p>
          <p className="text-[11px] text-muted-foreground mt-1">
            Event, then rep, then default, then the built in wording. Each box is
            checked on its own, so a rep who overrides only the text still gets the
            default subject. Leave a box blank to fall through to the next level.
            This applies to all four emails below, not just the text message.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-7 h-7 animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            {/* DEFAULT */}
            <Card className="p-4 bg-card mb-5">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-accent" />
                <h3 className="font-semibold text-foreground text-sm">Default Templates</h3>
              </div>
              <div className="space-y-3">
                <TemplateFields
                  idPrefix="default"
                  value={{ sms: defaultCfg.sms, subject: defaultCfg.emailSubject, body: defaultCfg.emailBody }}
                  onChange={next => setConfig(c => ({ ...c, default: { ...(c.default || {}), sms: next.sms, emailSubject: next.subject, emailBody: next.body } }))}
                  disabled={savingDefault}
                  showEmailPreview
                  previewRepId={previewRepId}
                />
                <Button onClick={saveDefault} disabled={savingDefault} className="w-full min-h-10 bg-accent hover:bg-accent/90 text-accent-foreground">
                  {savingDefault ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" />Save Defaults</>}
                </Button>
                <p className="text-[10px] text-muted-foreground">
                  Applies to the text message and Initial Email only. The three follow
                  up reminders still fall back to the built in wording below them; this
                  page has no per-rep or global editor for those yet, only per-event.
                </p>
              </div>
            </Card>

            {/* PER REP */}
            <Card className="p-4 bg-card mb-5">
              <div className="flex items-center justify-between gap-2 mb-3">
                <h3 className="font-semibold text-foreground text-sm">Per-Rep Override</h3>
                <Select value={selectedRep} onValueChange={setSelectedRep}>
                  <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {reps.map(r => <SelectItem key={r.id} value={r.id}>{r.full_name || r.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {selectedRepUser && !(selectedRepUser.calendar_links?.length) && (
                <div className="flex items-start gap-1.5 text-[11px] text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2 mb-3">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    {repName(selectedRep)} has no calendar links set. Every {'{calendarLink}'} line in
                    their templates, and the SMS, will be silently dropped until one is added on their profile.
                  </span>
                </div>
              )}
              <div className="space-y-3">
                <TemplateFields
                  idPrefix="rep"
                  value={{ sms: repCfg.sms, subject: repCfg.emailSubject, body: repCfg.emailBody }}
                  onChange={next => setConfig(c => ({ ...c, reps: { ...(c.reps || {}), [selectedRep]: { ...((c.reps && c.reps[selectedRep]) || {}), sms: next.sms, emailSubject: next.subject, emailBody: next.body } } }))}
                  disabled={savingRep}
                  overrideLabel={repName(selectedRep)}
                  resolved={{
                    sms: resolveSmsField(repCfg.sms, null, defaultCfg.sms, null),
                    subject: resolveEmailField(repCfg.emailSubject, null, defaultCfg.emailSubject, emailDefaults?.initial?.subject, null),
                    body: resolveEmailField(repCfg.emailBody, null, defaultCfg.emailBody, emailDefaults?.initial?.body, null),
                  }}
                  showEmailPreview
                  previewRepId={previewRepId}
                />
                <Button onClick={saveRep} disabled={savingRep} className="w-full min-h-10 bg-accent hover:bg-accent/90 text-accent-foreground">
                  {savingRep ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" />Save {repName(selectedRep)}</>}
                </Button>
              </div>
            </Card>

            {/* PER EVENT: all four emails */}
            <Card ref={eventPanelRef} className="p-4 bg-card mb-5">
              <div className="flex items-center justify-between gap-2 mb-3">
                <h3 className="font-semibold text-foreground text-sm">Per-Event Override</h3>
                <Select value={selectedShow} onValueChange={setSelectedShow}>
                  <SelectTrigger className="h-8 w-48 text-xs"><SelectValue placeholder="Select event" /></SelectTrigger>
                  <SelectContent>
                    {shows.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {selectedShow ? (
                <div className="space-y-3">
                  <p className="text-[10px] text-muted-foreground -mt-1">
                    A show has no rep of its own. The rep level shown below, when a box is
                    inherited, previews {repName(selectedRep)}'s templates, the rep selected
                    in Per-Rep Override above. A contact actually captured by a different rep
                    at this show would inherit that rep's own template instead.
                  </p>
                  <Tabs value={activeEmailTab} onValueChange={setActiveEmailTab}>
                    <TabsList className="grid grid-cols-4 w-full h-auto">
                      {EMAIL_KINDS.map(({ tab, label }) => (
                        <TabsTrigger key={tab} value={tab} className="text-[11px] px-1.5 py-1.5 leading-tight">
                          {label}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                    {EMAIL_KINDS.map(({ tab, kind, label, subjectField, bodyField }) => {
                      const { subjectKey, bodyKey } = repConfigKeysFor(tab);
                      const builtIn = emailDefaults?.[defaultsKeyFor(tab)];
                      return (
                      <TabsContent key={tab} value={tab}>
                        <EventEmailEditor
                          kind={kind}
                          label={label}
                          value={{
                            sms: eventTpl.sms_template,
                            subject: eventTpl[subjectField],
                            body: eventTpl[bodyField],
                          }}
                          onChange={(next) => setEventTpl(prev => ({
                            ...prev,
                            sms_template: next.sms,
                            [subjectField]: next.subject,
                            [bodyField]: next.body,
                          }))}
                          disabled={savingEvent}
                          defaults={builtIn}
                          overrideLabel={selectedShowName}
                          previewRepId={previewRepId}
                          show={previewShow}
                          resolved={{
                            sms: tab === 'initial'
                              ? resolveSmsField(eventTpl.sms_template, repCfg.sms, defaultCfg.sms, repName(selectedRep))
                              : null,
                            subject: resolveEmailField(eventTpl[subjectField], repCfg[subjectKey], defaultCfg[subjectKey], builtIn?.subject, repName(selectedRep)),
                            body: resolveEmailField(eventTpl[bodyField], repCfg[bodyKey], defaultCfg[bodyKey], builtIn?.body, repName(selectedRep)),
                          }}
                        />
                      </TabsContent>
                      );
                    })}
                  </Tabs>
                  <Button onClick={saveEvent} disabled={savingEvent} className="w-full min-h-10 bg-accent hover:bg-accent/90 text-accent-foreground">
                    {savingEvent ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" />Save Event</>}
                  </Button>
                  <p className="text-[10px] text-muted-foreground">
                    Saving writes all four emails and the text message at once, whichever
                    tab you are looking at.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">Select an event to customize its templates.</p>
              )}
            </Card>
            <div className="h-8" />
          </>
        )}
      </PageContainer>
    </div>
  );
}
